import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import { toast } from "react-toastify";
import { Trash2, X } from "lucide-react";
import facilityAboutService from "../services/facilityAboutService";
import facilityService from "../services/facilityService";
import facilityImageService from "../services/facilityImageService";
import { useDashboardUI } from "../context/GlobalContext";

const VERSION_MAX_LENGTH = 10;
// Mirrors the backend's MAX_IMAGE_SIZE_BYTES (app/api/routes/facility_images.py)
// -- checked here too so an oversized file is rejected instantly instead of
// only after the full direct-to-blob upload completes.
const MAX_IMAGE_SIZE_BYTES = 5 * 1024 * 1024;

const FIELDS = [
    { key: "version", label: "Version", type: "text", maxLength: VERSION_MAX_LENGTH },
    { key: "platform_verbiage", label: "Body", type: "textarea" },
    { key: "support_site_url", label: "Support site URL", type: "url" },
    { key: "release_notes_url", label: "Release notes URL", type: "url" },
    { key: "support_contact_name", label: "Contact name", type: "text" },
    { key: "support_contact_email", label: "Contact email", type: "email" },
    { key: "support_contact_phone", label: "Contact phone", type: "tel" },
];

// Mirrors the backend's own validation (app/schemas/facility_about_config.py)
// so the admin sees the same rejection inline instead of only after Save
// round-trips to a 422.
const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
const PHONE_RE = /^[0-9+\-\s().xX]{7,25}$/;

const validateFieldValue = (type, rawValue) => {
    const value = (rawValue || "").trim();
    if (!value) return null; // empty always clears the field, never invalid
    if (type === "email" && !EMAIL_RE.test(value)) return "Enter a valid email address.";
    if (type === "tel") {
        if (!PHONE_RE.test(value)) return "Enter a valid phone number.";
        let digits = value.replace(/\D/g, "");
        if (digits.length === 11 && digits.startsWith("1")) digits = digits.slice(1); // tolerate a leading US country code
        if (digits.length !== 10) return "Phone number must have 10 digits.";
    }
    if (type === "url") {
        try {
            const parsed = new URL(value);
            if (parsed.protocol !== "http:" && parsed.protocol !== "https:") {
                return "Must be a valid http:// or https:// URL.";
            }
        } catch {
            return "Must be a valid http:// or https:// URL.";
        }
    }
    return null;
};

// Empty string and null both mean "no value" -- normalize before diffing/sending
// so an untouched field never gets sent as a spurious change. Trimmed first so
// a stray space doesn't count as "set".
const normalize = (v) => {
    if (v === undefined) return null;
    if (typeof v === "string") {
        const trimmed = v.trim();
        return trimmed === "" ? null : trimmed;
    }
    return v;
};

// Minimal Markdown -> HTML for the Body live preview. Deliberately covers
// only the syntax our own toolbar produces (bold/italic/headings/bullets/
// images), not a general Markdown implementation -- keeps this dependency-free
// while still matching what the Frontend's real react-markdown renderer shows.
const escapeHtml = (str) => str.replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");

const formatInline = (text) => text.replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>").replace(/\*(.+?)\*/g, "<em>$1</em>");

// Plain Markdown has no image-size syntax, so a #w=NNN fragment is appended
// to the URL as our own convention -- harmless to the actual image request
// (fragments aren't sent to the server), parsed back out here and by the
// Frontend's real renderer (components/utility/about-dialog.tsx) alike.
const IMAGE_WIDTH_RE = /#w=(\d+)$/;
const parseImageUrl = (rawUrl) => {
    const m = rawUrl.match(IMAGE_WIDTH_RE);
    if (!m) return { src: rawUrl, width: null };
    return { src: rawUrl.slice(0, rawUrl.length - m[0].length), width: parseInt(m[1], 10) };
};

const renderMarkdownPreview = (markdown) => {
    if (!markdown) return "<p style=\"color:#9ca3af;font-style:italic;\">Nothing to preview yet.</p>";
    const lines = escapeHtml(markdown).split("\n");
    let html = "";
    let inList = false;
    const closeList = () => { if (inList) { html += "</ul>"; inList = false; } };

    lines.forEach((line) => {
        const image = line.match(/^!\[[^\]]*\]\(([^)]+)\)$/);
        const h3 = line.match(/^###\s+(.*)/);
        const h2 = line.match(/^##\s+(.*)/);
        const bullet = line.match(/^[-*]\s+(.*)/);

        if (bullet) {
            if (!inList) { html += "<ul style=\"margin:4px 0;padding-left:20px;list-style:disc;\">"; inList = true; }
            html += `<li style="list-style:disc;">${formatInline(bullet[1])}</li>`;
            return;
        }
        closeList();

        if (image) {
            const { src, width } = parseImageUrl(image[1]);
            const widthStyle = width ? `width:${width}px;max-width:100%;` : "max-width:100%;";
            html += `<img src="${src}" style="${widthStyle}border-radius:8px;margin:6px 0;" alt="" />`;
        } else if (h3) {
            html += `<h4 style="margin:8px 0 2px;font-weight:600;">${formatInline(h3[1])}</h4>`;
        } else if (h2) {
            html += `<h3 style="margin:10px 0 4px;font-weight:700;">${formatInline(h2[1])}</h3>`;
        } else if (line.trim() === "") {
            html += "<br/>";
        } else {
            html += `<p style="margin:2px 0;">${formatInline(line)}</p>`;
        }
    });
    closeList();
    return html;
};

export const FacilityAbout = () => {
    const { facilityNumber } = useParams();
    const navigate = useNavigate();
    const location = useLocation();
    const isGlobal = !facilityNumber;
    const { setPageLoader } = useDashboardUI();

    const [loading, setLoading] = useState(true);
    const [facilityName, setFacilityName] = useState(location.state?.facility?.name || null);
    const [values, setValues] = useState({});
    const [originalValues, setOriginalValues] = useState({});
    const [isFirstSave, setIsFirstSave] = useState(false);
    const [history, setHistory] = useState([]);
    const [showHistory, setShowHistory] = useState(false);
    const [openHistoryIndex, setOpenHistoryIndex] = useState(null);

    // Every image previously uploaded for this scope (facility or global) --
    // the catalog admins drag from to embed into the Body text. Not a
    // savable field itself -- purely an upload/preview tool.
    const [mediaLibrary, setMediaLibrary] = useState([]);
    const [uploading, setUploading] = useState(false);
    const verbiageRef = useRef(null);
    const [showVerbiagePreview, setShowVerbiagePreview] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState(null);

    useEffect(() => {
        if (!previewImageUrl) return;
        const onKeyDown = (e) => {
            if (e.key === "Escape") setPreviewImageUrl(null);
        };
        window.addEventListener("keydown", onKeyDown);
        return () => window.removeEventListener("keydown", onKeyDown);
    }, [previewImageUrl]);

    useEffect(() => {
        const load = async () => {
            try {
                if (isGlobal) {
                    const [data, images] = await Promise.all([
                        facilityAboutService.getGlobalAbout(),
                        facilityImageService.listImages(null),
                    ]);
                    const exists = Boolean(data?.id);
                    setIsFirstSave(!exists);
                    setHistory(exists ? (data.history || []) : []);
                    setMediaLibrary(images || []);
                    const v = {};
                    FIELDS.forEach(({ key }) => { v[key] = exists ? (data[key] ?? "") : ""; });
                    setValues(v);
                    setOriginalValues(v);
                } else {
                    const [facilityData, facilityRecord, images] = await Promise.all([
                        facilityAboutService.getFacilityAboutRaw(facilityNumber),
                        facilityService.getFacilityByNumber(facilityNumber).catch(() => null),
                        facilityImageService.listImages(facilityNumber),
                    ]);
                    if (facilityRecord?.name) setFacilityName(facilityRecord.name);
                    const facilityExists = Boolean(facilityData?.id);
                    setIsFirstSave(!facilityExists);
                    setHistory(facilityExists ? (facilityData.history || []) : []);
                    setMediaLibrary(images || []);

                    const v = {};
                    FIELDS.forEach(({ key }) => { v[key] = facilityExists ? (facilityData[key] ?? "") : ""; });
                    setValues(v);
                    setOriginalValues(v);
                }
            } catch (error) {
                console.error("Error loading About config:", error);
                toast.error("Failed to load About page config.");
            } finally {
                setLoading(false);
            }
        };
        load();
    }, [facilityNumber, isGlobal]);

    const handleChange = (key, value) => {
        setValues((prev) => ({ ...prev, [key]: value }));
    };

    // Plain-textarea Markdown formatting -- no rich-text editor dependency.
    // Wraps/prefixes the current selection in verbiageRef and re-renders via
    // handleChange; the About page (Frontend) already renders platform_verbiage
    // as Markdown, so ** * # - are all it takes to get bold/italic/headings/lists.
    const wrapVerbiageSelection = (prefix, suffix = prefix, placeholder = "text") => {
        const el = verbiageRef.current;
        if (!el) return;
        const { selectionStart: start, selectionEnd: end, value } = el;
        const selected = value.slice(start, end) || placeholder;
        const newValue = value.slice(0, start) + prefix + selected + suffix + value.slice(end);
        handleChange("platform_verbiage", newValue);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(start + prefix.length, start + prefix.length + selected.length);
        });
    };

    const prefixVerbiageLines = (linePrefix, placeholder = "text") => {
        const el = verbiageRef.current;
        if (!el) return;
        const { selectionStart: start, selectionEnd: end, value } = el;
        const lineStart = value.lastIndexOf("\n", start - 1) + 1;
        const lineEndSearch = value.indexOf("\n", end);
        const lineEnd = lineEndSearch === -1 ? value.length : lineEndSearch;
        const block = value.slice(lineStart, lineEnd);
        const isBlockEmpty = block.trim() === "";

        // Nothing selected/typed on this line yet -- behave like Bold/Italic
        // and drop in a pre-selected placeholder instead of a no-op.
        if (isBlockEmpty) {
            const newBlock = linePrefix + placeholder;
            const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
            handleChange("platform_verbiage", newValue);
            requestAnimationFrame(() => {
                el.focus();
                const selStart = lineStart + linePrefix.length;
                el.setSelectionRange(selStart, selStart + placeholder.length);
            });
            return;
        }

        const alreadyPrefixed = block.split("\n").every((line) => line === "" || line.startsWith(linePrefix));
        const newBlock = block
            .split("\n")
            .map((line) => {
                if (alreadyPrefixed) return line.startsWith(linePrefix) ? line.slice(linePrefix.length) : line;
                return line === "" ? line : linePrefix + line;
            })
            .join("\n");
        const newValue = value.slice(0, lineStart) + newBlock + value.slice(lineEnd);
        handleChange("platform_verbiage", newValue);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(lineStart, lineStart + newBlock.length);
        });
    };

    // Embeds a catalog image inline in the verbiage text via Markdown image syntax.
    const insertImageIntoVerbiage = (url) => {
        const markdown = `![](${url})`;
        const el = verbiageRef.current;
        if (!el) {
            setValues((prev) => {
                const existing = prev.platform_verbiage || "";
                return { ...prev, platform_verbiage: existing ? `${existing}\n\n${markdown}\n` : `${markdown}\n` };
            });
            return;
        }
        const { selectionStart: start, selectionEnd: end, value } = el;
        const newValue = value.slice(0, start) + markdown + value.slice(end);
        handleChange("platform_verbiage", newValue);
        requestAnimationFrame(() => {
            el.focus();
            const pos = start + markdown.length;
            el.setSelectionRange(pos, pos);
        });
    };

    // Chrome positions a textarea's cursor at the drop point before firing
    // `drop`, so selectionStart/End here already reflects where the image
    // was dropped -- this doesn't hold reliably in every browser, since plain
    // <textarea> elements don't expose real pixel-to-caret drop mapping.
    const handleVerbiageDrop = (e) => {
        e.preventDefault();
        const url = e.dataTransfer.getData("text/plain");
        if (url) insertImageIntoVerbiage(url);
    };

    // Resizes the image on the cursor's current line by adding/adjusting a
    // #w=NNN suffix on its URL -- our own convention since plain Markdown
    // has no image-size syntax (see parseImageUrl / IMAGE_WIDTH_RE above).
    const resizeSelectedImage = (delta) => {
        const el = verbiageRef.current;
        if (!el) return;
        const { selectionStart: start, selectionEnd: end, value } = el;
        const lineStart = value.lastIndexOf("\n", start - 1) + 1;
        const lineEndSearch = value.indexOf("\n", end);
        const lineEnd = lineEndSearch === -1 ? value.length : lineEndSearch;
        const line = value.slice(lineStart, lineEnd);
        const match = line.match(/^!\[[^\]]*\]\(([^)]+)\)$/);
        if (!match) {
            toast.info("Click into an image line first, then resize it.");
            return;
        }
        const { src, width } = parseImageUrl(match[1]);
        const newWidth = Math.min(1200, Math.max(100, (width || 400) + delta));
        const newLine = `![](${src}#w=${newWidth})`;
        const newValue = value.slice(0, lineStart) + newLine + value.slice(lineEnd);
        handleChange("platform_verbiage", newValue);
        requestAnimationFrame(() => {
            el.focus();
            el.setSelectionRange(lineStart, lineStart + newLine.length);
        });
    };

    const handleUploadImage = async (file) => {
        if (!file) return;
        if (file.size > MAX_IMAGE_SIZE_BYTES) {
            toast.error(`"${file.name}" exceeds the ${MAX_IMAGE_SIZE_BYTES / (1024 * 1024)} MB size limit.`);
            return;
        }
        try {
            setUploading(true);
            const uploaded = await facilityImageService.uploadImage(isGlobal ? null : facilityNumber, file);
            setMediaLibrary((prev) => [{ id: uploaded.id, url: uploaded.url, filename: uploaded.filename }, ...prev]);
            toast.success("Image uploaded!");
        } catch (error) {
            console.error("Error uploading image:", error);
            const detail = error?.response?.data?.detail;
            toast.error(typeof detail === "string" ? `Failed to upload image: ${detail}` : "Failed to upload image.");
        } finally {
            setUploading(false);
        }
    };

    const handleDeleteImage = async (image) => {
        // Embedded as ![](url) or ![](url#w=400) (see IMAGE_WIDTH_RE) -- a
        // plain substring check catches both, since the base URL is always
        // present either way.
        const isUsedInBody = Boolean(image.url) && (values.platform_verbiage || "").includes(image.url);
        if (isUsedInBody) {
            toast.error(`"${image.filename || "This image"}" is currently used in the Body text -- deleting it will leave a broken image there.`);
        }
        const confirmMessage = isUsedInBody
            ? `"${image.filename || "This image"}" is used in the Body text below. Delete it anyway? The embedded image will break.`
            : `Delete "${image.filename || "this image"}" from the catalog? This can't be undone.`;
        if (!window.confirm(confirmMessage)) return;
        try {
            await facilityImageService.deleteImage(isGlobal ? null : facilityNumber, image.id);
            setMediaLibrary((prev) => prev.filter((img) => img.id !== image.id));
            toast.success("Image deleted.");
        } catch (error) {
            console.error("Error deleting image:", error);
            toast.error("Failed to delete image.");
        }
    };

    const buildPayload = () => {
        const payload = {};
        FIELDS.forEach(({ key }) => {
            const cur = normalize(values[key]);
            const orig = normalize(originalValues[key]);
            if (cur !== orig) payload[key] = cur;
        });
        return payload;
    };

    const payload = buildPayload();
    const hasChanges = Object.keys(payload).length > 0;

    const fieldErrors = {};
    FIELDS.forEach(({ key, type }) => {
        const err = validateFieldValue(type, values[key]);
        if (err) fieldErrors[key] = err;
    });
    const hasErrors = Object.keys(fieldErrors).length > 0;

    // Warn before losing edits on a tab close/refresh, and on the two
    // in-app navigation actions this page offers (Back, platform default).
    useEffect(() => {
        const handler = (e) => {
            if (!hasChanges) return;
            e.preventDefault();
            e.returnValue = "";
        };
        window.addEventListener("beforeunload", handler);
        return () => window.removeEventListener("beforeunload", handler);
    }, [hasChanges]);

    const navigateGuarded = (path) => {
        if (hasChanges && !window.confirm("You have unsaved changes. Leave without saving?")) return;
        navigate(path);
    };

    const handleSave = async () => {
        if (!hasChanges || hasErrors) return;
        try {
            setPageLoader(true);
            let resp;
            if (isGlobal) {
                resp = isFirstSave
                    ? await facilityAboutService.createFacilityAbout({ facility_id: null, ...payload })
                    : await facilityAboutService.updateGlobalAbout(payload);
            } else {
                resp = isFirstSave
                    ? await facilityAboutService.createFacilityAbout({ facility_id: parseInt(facilityNumber), ...payload })
                    : await facilityAboutService.updateFacilityAbout(facilityNumber, payload);
            }
            toast.success("About page saved!");
            setIsFirstSave(false);
            setHistory(resp?.history || []);

            const v = {};
            FIELDS.forEach(({ key }) => { v[key] = resp?.[key] ?? ""; });
            setValues(v);
            setOriginalValues(v);
        } catch (error) {
            console.error("Error saving About config:", error);
            const detail = error?.response?.data?.detail;
            const message = Array.isArray(detail)
                ? detail.map((d) => d.msg).join(" ")
                : typeof detail === "string"
                ? detail
                : null;
            toast.error(message ? `Failed to save About page config: ${message}` : "Failed to save About page config.");
        } finally {
            setPageLoader(false);
        }
    };

    // History revert loads the prior values back into the form -- matching
    // FacilityTheme's convention, the admin still has to click Save.
    const handleRevertEntry = (entry) => {
        const changedFields = entry.changed_fields || [];
        const previousValues = entry.previous_values || {};
        setValues((prev) => {
            const next = { ...prev };
            changedFields.forEach((key) => {
                next[key] = previousValues[key] ?? "";
            });
            return next;
        });
        toast.info("Reverted. Don't forget to save!");
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    if (loading) {
        return <p className="text-center mt-10">Loading About page configuration...</p>;
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-10">
            <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-8 mt-4 sm:mt-8">
                <div className="mb-6 border-b pb-4 flex flex-wrap justify-between items-center gap-2">
                    <h2 className="min-w-0 break-words text-xl sm:text-2xl font-semibold text-gray-800">
                        {isGlobal ? "About Page" : `About Page — ${facilityName || `Facility ${facilityNumber}`}`}
                    </h2>
                    <button
                        onClick={() => navigateGuarded("/facility")}
                        className="text-purple-600 hover:text-purple-800 text-sm font-medium"
                    >
                        ← Back
                    </button>
                </div>

                {!isGlobal && (
                    <p className="text-sm text-gray-500 mb-6">
                        {isFirstSave
                            ? "This facility is currently showing the platform default About page. Save any change below to give it its own — once saved, fields left blank here show as blank, not the platform default."
                            : "This facility has its own About page. Fields left blank here show as blank — they no longer fall back to the "}
                        {!isFirstSave && (
                            <button onClick={() => navigateGuarded("/about-me/global")} className="text-purple-600 underline">
                                platform default
                            </button>
                        )}
                        {!isFirstSave && "."}
                    </p>
                )}

                {/* Image catalog -- upload/preview/drag-to-embed only, not a savable field itself */}
                <div className="mb-6">
                    <label className="block text-sm font-medium text-gray-700 mb-1">Image catalog</label>
                    <p className="mb-2 text-xs text-gray-400">
                        Click an image to preview it full-size. Drag it into the Body text below to embed it there.
                    </p>
                    <div className="flex flex-wrap gap-3 mb-3">
                        {mediaLibrary.map((img) => (
                            <div
                                key={img.id}
                                draggable
                                onDragStart={(e) => e.dataTransfer.setData("text/plain", img.url)}
                                onClick={() => setPreviewImageUrl(img.url)}
                                className="group relative h-20 w-20 cursor-pointer overflow-hidden rounded-lg border-2 border-transparent hover:border-purple-300"
                                title="Click to preview -- drag onto the Body text to embed it there"
                            >
                                <img src={img.url} alt={img.filename || ""} className="h-full w-full object-cover" />
                                <button
                                    type="button"
                                    onClick={(e) => {
                                        e.stopPropagation();
                                        handleDeleteImage(img);
                                    }}
                                    title="Delete from catalog"
                                    className="absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full bg-black/60 text-white opacity-0 transition-opacity hover:bg-red-600 group-hover:opacity-100"
                                >
                                    <Trash2 size={12} />
                                </button>
                            </div>
                        ))}
                        {mediaLibrary.length === 0 && (
                            <p className="text-sm italic text-gray-400">No images uploaded yet.</p>
                        )}
                    </div>
                    <label
                        className={`inline-block cursor-pointer rounded-lg border px-4 py-2 text-sm font-medium ${uploading ? "border-gray-200 text-gray-400" : "border-purple-300 text-purple-600 hover:bg-purple-50"
                            }`}
                    >
                        {uploading ? "Uploading…" : "+ Upload image"}
                        <input
                            type="file"
                            accept="image/png, image/jpeg, image/webp, image/svg+xml, image/gif"
                            className="hidden"
                            disabled={uploading}
                            onChange={(e) => {
                                const file = e.target.files?.[0];
                                e.target.value = "";
                                handleUploadImage(file);
                            }}
                        />
                    </label>
                </div>

                {/* Grid Inputs - 1 per row */}
                <div className="grid grid-cols-1 gap-4">
                    {FIELDS.map(({ key, label, type, maxLength }) => (
                        <div key={key}>
                            <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
                            {type === "textarea" ? (
                                <div>
                                    {key === "platform_verbiage" && (
                                        <div className="mb-1.5 flex flex-col-reverse gap-2 sm:flex-row sm:flex-wrap sm:items-center sm:justify-between">
                                            {!showVerbiagePreview && (
                                                <div className="flex flex-wrap gap-1">
                                                    <button type="button" title="Bold" onClick={() => wrapVerbiageSelection("**", "**", "bold text")} className="w-8 h-8 rounded border text-sm font-bold text-gray-700 hover:bg-gray-100">B</button>
                                                    <button type="button" title="Italic" onClick={() => wrapVerbiageSelection("*", "*", "italic text")} className="w-8 h-8 rounded border text-sm italic text-gray-700 hover:bg-gray-100">I</button>
                                                    <button type="button" title="Heading" onClick={() => prefixVerbiageLines("## ", "Heading text")} className="w-8 h-8 rounded border text-xs font-semibold text-gray-700 hover:bg-gray-100">H2</button>
                                                    <button type="button" title="Subheading" onClick={() => prefixVerbiageLines("### ", "Subheading text")} className="w-8 h-8 rounded border text-xs font-semibold text-gray-700 hover:bg-gray-100">H3</button>
                                                    <button type="button" title="Bullet list" onClick={() => prefixVerbiageLines("- ", "List item")} className="w-8 h-8 rounded border text-sm text-gray-700 hover:bg-gray-100">•</button>
                                                    <span className="mx-1 w-px self-stretch bg-gray-200" />
                                                    <button type="button" title="Shrink image on this line" onClick={() => resizeSelectedImage(-100)} className="h-8 whitespace-nowrap rounded border px-2 text-xs text-gray-700 hover:bg-gray-100">Img −</button>
                                                    <button type="button" title="Enlarge image on this line" onClick={() => resizeSelectedImage(100)} className="h-8 whitespace-nowrap rounded border px-2 text-xs text-gray-700 hover:bg-gray-100">Img +</button>
                                                </div>
                                            )}
                                            <div className="flex w-full overflow-hidden rounded-lg border text-xs sm:ml-auto sm:w-auto">
                                                <button
                                                    type="button"
                                                    onClick={() => setShowVerbiagePreview(false)}
                                                    className={`flex-1 sm:flex-none px-3 py-1.5 sm:py-1 ${!showVerbiagePreview ? "bg-purple-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                                >
                                                    Write
                                                </button>
                                                <button
                                                    type="button"
                                                    onClick={() => setShowVerbiagePreview(true)}
                                                    className={`flex-1 sm:flex-none px-3 py-1.5 sm:py-1 ${showVerbiagePreview ? "bg-purple-600 text-white" : "text-gray-600 hover:bg-gray-100"}`}
                                                >
                                                    Preview
                                                </button>
                                            </div>
                                        </div>
                                    )}
                                    {key === "platform_verbiage" && showVerbiagePreview ? (
                                        <div
                                            className="min-h-[280px] w-full rounded-lg border bg-white px-4 py-2 text-sm text-gray-900"
                                            dangerouslySetInnerHTML={{ __html: renderMarkdownPreview(values[key]) }}
                                        />
                                    ) : (
                                        <textarea
                                            ref={key === "platform_verbiage" ? verbiageRef : undefined}
                                            value={values[key] ?? ""}
                                            onChange={(e) => handleChange(key, e.target.value)}
                                            onDragOver={key === "platform_verbiage" ? (e) => e.preventDefault() : undefined}
                                            onDrop={key === "platform_verbiage" ? handleVerbiageDrop : undefined}
                                            placeholder={key === "platform_verbiage" ? "Drag an image from the catalog above to embed it here." : ""}
                                            rows={key === "platform_verbiage" ? 12 : 4}
                                            className="w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 focus:ring-purple-400 placeholder:text-gray-400 placeholder:italic"
                                        />
                                    )}
                                    {key === "platform_verbiage" && !showVerbiagePreview && (
                                        <p className="mt-1 text-xs text-gray-400">Supports Markdown -- **bold**, *italic*, ## headings, - lists.</p>
                                    )}
                                </div>
                            ) : (
                                <div>
                                    <input
                                        type={type}
                                        maxLength={maxLength}
                                        value={values[key] ?? ""}
                                        onChange={(e) => handleChange(key, e.target.value)}
                                        className={`w-full px-4 py-2 border rounded-lg bg-gray-50 focus:outline-none focus:ring-2 ${
                                            fieldErrors[key] ? "border-red-400 focus:ring-red-300" : "focus:ring-purple-400"
                                        }`}
                                    />
                                    {maxLength && (
                                        <p className="mt-1 text-right text-xs text-gray-400">
                                            {(values[key] || "").length}/{maxLength}
                                        </p>
                                    )}
                                    {fieldErrors[key] && (
                                        <p className="mt-1 text-xs text-red-600">{fieldErrors[key]}</p>
                                    )}
                                </div>
                            )}
                        </div>
                    ))}
                </div>

                <div className="flex flex-wrap justify-end items-center mt-8 gap-2 sm:gap-4">
                    {history && history.length > 0 && (
                        <button
                            onClick={() => setShowHistory((prev) => !prev)}
                            className="px-4 sm:px-6 py-2 border border-purple-500 text-purple-600 rounded-full font-semibold shadow hover:bg-purple-50 transition"
                        >
                            {showHistory ? "Hide History" : `View History (${history.length})`}
                        </button>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={!hasChanges || hasErrors}
                        title={hasErrors ? "Fix the highlighted field(s) before saving" : undefined}
                        className={`px-4 sm:px-6 py-2 rounded-full font-semibold shadow transition
                            ${hasChanges && !hasErrors
                                ? "bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:opacity-95 cursor-pointer"
                                : "bg-gray-300 text-gray-500 cursor-not-allowed"}`}
                    >
                        Save changes
                    </button>
                </div>
            </div>

            {showHistory && (
                <div className="max-w-5xl mx-auto bg-white rounded-lg shadow-lg p-4 sm:p-8 mt-4 sm:mt-8">
                    <div className="flex justify-between items-center border-b pb-3 mb-6">
                        <h2 className="text-2xl font-semibold text-gray-800">Edit History</h2>
                    </div>
                    <div className="space-y-2">
                        {history.map((entry, idx) => {
                            const isOpen = openHistoryIndex === idx;
                            return (
                                <div key={idx} className="border rounded-lg">
                                    <div className="flex flex-wrap justify-between items-center gap-2 p-3">
                                        <button
                                            type="button"
                                            onClick={() => setOpenHistoryIndex(isOpen ? null : idx)}
                                            className="flex min-w-0 items-center gap-2 text-left text-sm"
                                        >
                                            <svg
                                                xmlns="http://www.w3.org/2000/svg"
                                                width="14"
                                                height="14"
                                                viewBox="0 0 24 24"
                                                fill="none"
                                                stroke="currentColor"
                                                strokeWidth="3"
                                                strokeLinecap="round"
                                                strokeLinejoin="round"
                                                className="shrink-0 text-gray-400"
                                                style={{ transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform 0.2s" }}
                                            >
                                                <path d="M6 9l6 6l6-6"></path>
                                            </svg>
                                            <div>
                                                <div className="font-medium text-gray-800">
                                                    {(entry.changed_fields || []).map((f) => FIELDS.find((fld) => fld.key === f)?.label || f).join(", ")}
                                                </div>
                                                <div className="text-gray-500">
                                                    {entry.updated_at ? new Date(entry.updated_at).toLocaleString() : "Unknown date"}
                                                    {entry.updated_by_email && ` · ${entry.updated_by_email}`}
                                                </div>
                                            </div>
                                        </button>
                                        <button
                                            onClick={() => handleRevertEntry(entry)}
                                            className="ml-0 sm:ml-4 shrink-0 px-4 py-1.5 rounded-full text-sm font-semibold bg-gradient-to-r from-purple-500 to-purple-600 text-white hover:opacity-95"
                                        >
                                            Revert
                                        </button>
                                    </div>
                                    {isOpen && (
                                        <div className="border-t bg-gray-50 p-3 space-y-3">
                                            {(entry.changed_fields || []).map((field) => (
                                                <div key={field}>
                                                    <div className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
                                                        {FIELDS.find((fld) => fld.key === field)?.label || field} — previous value
                                                    </div>
                                                    <div className="whitespace-pre-wrap break-words text-sm text-gray-700">
                                                        {entry.previous_values?.[field]
                                                            ? entry.previous_values[field]
                                                            : <span className="italic text-gray-400">(empty)</span>}
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            );
                        })}
                    </div>
                </div>
            )}

            {previewImageUrl && (
                <div
                    className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 p-3 sm:p-8"
                    onClick={() => setPreviewImageUrl(null)}
                >
                    <img
                        src={previewImageUrl}
                        alt=""
                        onClick={(e) => e.stopPropagation()}
                        className="max-h-full max-w-full rounded-lg shadow-2xl"
                    />
                    <button
                        type="button"
                        onClick={() => setPreviewImageUrl(null)}
                        className="absolute right-6 top-6 flex h-9 w-9 items-center justify-center rounded-full bg-white/90 text-gray-800 hover:bg-white"
                        title="Close (Esc)"
                    >
                        <X size={18} />
                    </button>
                </div>
            )}
        </div>
    );
};
